import { Link, useLocation } from '@tanstack/react-router'
import { Menu, X, Globe } from 'lucide-react'
import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/application/stores/auth.store'
import { useGetMe } from '@/application/services/user.service'
import { UserAvatar } from '@/ui/components/common/UserAvatar'
import { Button } from '@/ui/components/common/Button'
import { classNames } from '@/ui/utils/main'
import { useGetSiteIdentity } from '@/application/services/settings.service'
import { useCurrentTheme } from '@/application/hooks/useCurrentTheme'
import { PRIMARY_NAV_SECTIONS } from '@/application/seo/navigation'
import { PublicThemeToggle } from '@/ui/components/public/PublicThemeToggle'

const LANGUAGES = [
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
] as const

export function PublicNavbar() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuthStore()
  const resolvedLang = i18n.language.substring(0, 2).toLowerCase()
  const navLang = ['fr', 'en', 'es'].includes(resolvedLang) ? resolvedLang : 'fr'
  const { data: settings } = useGetSiteIdentity(navLang)
  const theme = useCurrentTheme()
  const effectiveName = settings?.siteName || t('app_name')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const location = useLocation()
  const langRef = useRef<HTMLDivElement>(null)
  const { data: profile } = useGetMe({ enabled: !!user })

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        langRef.current &&
        event.target instanceof Node &&
        !langRef.current.contains(event.target)
      ) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentLang = LANGUAGES.find((lang) => lang.code === i18n.language) ?? LANGUAGES[0]

  const isAdminOrStreamer = user?.role === 'admin' || user?.role === 'streamer'
  const userDestination = isAdminOrStreamer ? '/manage/dashboard' : '/manage/profile'

  const navLinks = PRIMARY_NAV_SECTIONS.map((section) => ({
    label: t(section.labelKey),
    href: section.path,
  }))

  return (
    <nav className="site-navbar sticky top-0 z-50 border-b border-border bg-card pt-safe-top print:hidden">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline hover:no-underline">
            <img src={theme.logoUrl} alt="" aria-hidden="true" className="h-9 w-9" />
            <span className="font-serif text-xl font-bold text-foreground">{effectiveName}</span>
          </Link>

          {/*
            Desktop nav.
            Gated on `desktop:` — a plain 1024px width breakpoint
            (tailwind.config.js), wider than `md:`'s 768px, so the row (logo +
            nav links + lang selector + theme toggle + auth buttons) has room
            to breathe and landscape phones (up to ~956px wide) stay on the
            mobile nav. The burger further down carries the exact complement
            of this gate, so the two can never both show or both hide.
          */}
          <div className="hidden desktop:flex items-center">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={classNames(
                  'text-sm font-medium uppercase transition-colors no-underline hover:no-underline px-3 py-1.5 rounded',
                  location.pathname === link.href ||
                    (link.href !== '/' && location.pathname.startsWith(link.href))
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side — same gate as the nav links above. */}
          <div className="hidden desktop:flex items-center gap-3">
            {/* Language selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((prev) => !prev)}
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border border-border hover:border-foreground/30"
              >
                <Globe className="h-3 w-3" />
                {currentLang.label}
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 w-28 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code)
                        setLangOpen(false)
                      }}
                      className={classNames(
                        'w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground transition-colors text-left',
                        i18n.language === lang.code
                          ? 'font-semibold text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      <span>{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <PublicThemeToggle />

            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to={userDestination}
                  className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors no-underline hover:no-underline px-2 py-1 rounded hover:bg-accent hover:text-accent-foreground"
                >
                  <UserAvatar
                    displayName={user.displayName}
                    username={user.username}
                    avatarUrl={profile?.avatarUrl}
                    size="sm"
                  />
                  <span className="hidden lg:inline">{user.displayName || user.username}</span>
                </Link>
                <Button size="sm" variant="outline" className="uppercase" onClick={logout}>
                  {t('nav.logout')}
                </Button>
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button size="sm" variant="ghost" className="uppercase">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="uppercase">
                    {t('nav.register')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button — the exact complement of the `desktop:`
              (1024px) gate on the nav links, so a landscape phone keeps the
              burger. */}
          <button className="desktop:hidden p-2" onClick={() => setMobileOpen((prev) => !prev)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="desktop:hidden border-t border-border py-2">
            {/* Header : langue + thème à droite */}
            <div className="flex justify-end items-center pb-2 gap-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={classNames(
                    'text-xs px-2 py-1 rounded transition-colors',
                    i18n.language === lang.code
                      ? 'font-semibold text-foreground border border-border'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
              <PublicThemeToggle />
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={classNames(
                  'block text-sm font-medium uppercase no-underline hover:no-underline py-1.5 px-3 rounded transition-colors',
                  location.pathname === link.href ||
                    (link.href !== '/' && location.pathname.startsWith(link.href))
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-2">
              {/* User / auth en bas à droite */}
              <div className="flex justify-end items-center gap-2">
                {user ? (
                  <>
                    <Link
                      to={userDestination}
                      className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground no-underline hover:no-underline"
                      onClick={() => setMobileOpen(false)}
                    >
                      <UserAvatar
                        displayName={user.displayName}
                        username={user.username}
                        avatarUrl={profile?.avatarUrl}
                        size="sm"
                      />
                      {user.displayName || user.username}
                    </Link>
                    <Button size="sm" variant="outline" className="uppercase" onClick={logout}>
                      {t('nav.logout')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)}>
                      <Button size="sm" variant="ghost" className="uppercase">
                        {t('nav.login')}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)}>
                      <Button size="sm" className="uppercase">
                        {t('nav.register')}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
